import user from "assets/images/user-dashboard.svg";
import chefs from "assets/images/dashboard-chefs.svg";
import retailer from "assets/images/dashboard-retailer.svg";
import arrowUp from "assets/images/arrow up.svg";
import arrowDown from "assets/images/arrow-down.svg";
import { FC, memo, useMemo } from "react";
import { Container, TopTiles, InfoTile } from "./style";
import {
  DashboardOrderOverviewChart,
  DashboardSessionOverviewChart,
} from "components/Graph/index";
import AreaChart from "components/Graph/area-chart";
import { useDashboard } from "./useHook";

interface DashboardProps { }

// Memoized Top Tiles Component
const TopTilesSection = memo(({ userStats }: { userStats: any }) => {
  const DashboardTopTilesData = useMemo(() => [
    {
      icon: chefs,
      name: "Total Customers",
      value: userStats.totalCustomers?.count || 0,
      percentage: `${Math.round(userStats.totalCustomers?.percentage || 0)}%`,
      image: arrowUp,
      color: "#292D32",
    },
    {
      icon: chefs,
      name: "Total Home Cooks",
      value: userStats.totalChefs?.count || 0,
      percentage: `${Math.round(userStats.totalChefs?.percentage || 0)}%`,
      image: arrowDown,
      color: "#FF2121",
    },
    {
      icon: retailer,
      name: "Total Retailers",
      value: userStats.totalRetailers?.count || 0,
      percentage: `${Math.round(userStats.totalRetailers?.percentage || 0)}%`,
      image: arrowDown,
      color: "#FF2121",
    },
    {
      icon: user,
      name: "Total Users",
      value: userStats.totalUsers?.count || 0,
      percentage: `${Math.round(userStats.totalUsers?.percentage || 0)}%`,
      image: arrowUp,
      color: "#292D32",
    },
  ], [userStats]);

  return (
    <TopTiles>
      {DashboardTopTilesData.map((item, index) => (
        <InfoTile
          key={index}
          last={index === DashboardTopTilesData.length - 1}
        >
          <img src={item.icon} alt={item.name} />
          <div>
            <h5>{item.name}</h5>
            <h1>{item.value.toLocaleString()}</h1>
          </div>
        </InfoTile>
      ))}
    </TopTiles>
  );
});

// Memoized Sales Graph Component
const SalesGraphSection = memo(() => {
  const { salesPeriod, setSalesPeriod, overviewText, salesGraphStats } = useDashboard().useSalesGraph();

  const handlePeriodChange = useMemo(() => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSalesPeriod(e.target.value);
  }, [setSalesPeriod]);

  return (
    <div className="graph-left">
      <div className="title">
        <h1>Overview</h1>
        <div className="dropdown-container">
          <select
            name="sales"
            id="sales"
            value={salesPeriod}
            onChange={handlePeriodChange}
          >
            <option value="thisWeek">This week</option>
            <option value="twoWeeks">Two weeks</option>
            <option value="thisMonth">This month</option>
            <option value="thisYear">This Year</option>
          </select>
        </div>
      </div>
      <AreaChart
        overviewText={overviewText}
        graphStats={{ salesGraphStats }}
      />
    </div>
  );
});

// Memoized Orders Overview Component
const OrdersOverviewSection = memo(() => {
  const { ordersPeriod, setOrdersPeriod, orderRatio } = useDashboard().useOrdersOverview();

  const handlePeriodChange = useMemo(() => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setOrdersPeriod(e.target.value);
  }, [setOrdersPeriod]);

  // const getPerformanceTextWithFallback = useMemo(() => (ratio: number): string => {
  //   if (ratio === 0) return "Nil";
  //   if (ratio > 80) return "Excellent";
  //   if (ratio > 70) return "Satisfactory";
  //   if (ratio > 60) return "Good";
  //   return "Average";
  // }, []);

  // console.log(orderRatio, "order ratio"); 
  const getPerformanceTextWithFallback = useMemo(
    () => (ratio: number): string => {
      if (ratio === 0) return "Nil";
      if (ratio >= 1 && ratio <= 20) return "Minor";
      if (ratio >= 21 && ratio <= 40) return "Modest";
      if (ratio >= 41 && ratio <= 60) return "Good";
      if (ratio >= 61 && ratio <= 80) return "Great";
      if (ratio >= 81 && ratio <= 100) return "Perfect";
      return "Nil"; // fallback for >100 or invalid
    },
    []
  );

  console.log(orderRatio, "order ratio");



  return (
    <div className="graph-cart">
      <div className="title">
        <h1>Orders Overview </h1>
        <div className="dropdown-container">
          <select
            name="orders"
            id="orders"
            value={ordersPeriod}
            onChange={handlePeriodChange}
          >
            <option value="thisWeek">This week</option>
            <option value="twoWeeks">Two weeks</option>
            <option value="thisMonth">This month</option>
            <option value="thisYear">This Year</option>
          </select>
        </div>
      </div>
      <div className="graph">
        <DashboardOrderOverviewChart
          text={getPerformanceTextWithFallback(orderRatio)}
          percentage={orderRatio}
          color={"#1B473B"}
        />
      </div>4
    </div>
  );
});

// Memoized Sessions Overview Component
const SessionsOverviewSection = memo(() => {
  const { sessionsPeriod, setSessionsPeriod, sessionRatio } = useDashboard().useSessionsOverview();

  const handlePeriodChange = useMemo(() => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSessionsPeriod(e.target.value);
  }, [setSessionsPeriod]);

  const getPerformanceTextWithFallback = useMemo(
    () => (ratio: number): string => {
      if (ratio === 0) return "Nil";
      if (ratio >= 1 && ratio <= 20) return "Minor";
      if (ratio >= 21 && ratio <= 40) return "Modest";
      if (ratio >= 41 && ratio <= 60) return "Good";
      if (ratio >= 61 && ratio <= 80) return "Great";
      if (ratio >= 81 && ratio <= 100) return "Perfect";
      return "Nil"; // fallback for >100 or invalid
    },
    []
  );

  return (
    <div className="graph-cart">
      <div className="title">
        <h1>Session Overview </h1>
        <div className="dropdown-container">
          <select
            name="sessions"
            id="sessions"
            value={sessionsPeriod}
            onChange={handlePeriodChange}
          >
            <option value="thisWeek">This week</option>
            <option value="twoWeeks">Two weeks</option>
            <option value="thisMonth">This month</option>
            <option value="thisYear">This Year</option>
          </select>
        </div>
      </div>
      <div className="graph">
        <DashboardSessionOverviewChart
          text={getPerformanceTextWithFallback(sessionRatio)}
          percentage={sessionRatio}
          color="#00B087"
        />
      </div>
    </div>
  );
});

const Dashboard: FC<DashboardProps> = () => {
  const { useUserStats } = useDashboard();
  const { userStats, isLoading } = useUserStats();

  if (isLoading || !userStats) {
    return <div>Loading...</div>;
  }

  return (
    <Container>
      <div className="title">
        <h1>Dashboard</h1>
      </div>
      <TopTilesSection userStats={userStats} />
      <div className="graph-container">
        <SalesGraphSection />
        <div className="graph-right">
          <OrdersOverviewSection />
          <SessionsOverviewSection />
        </div>
      </div>
    </Container>
  );
};

export default Dashboard;
